import { getWmsMenuTree } from './wms-menu';
import {
    buildPermissionTree,
    getPermissionDisplayLabel,
    isChildPermissionValue,
    normalizePermissions,
} from './permission-groups';

/**
 * Organize SF permission picklist options as Category → Task using the WMS menu tree.
 * RMA (and similar) child options stay under their parent task.
 * Picklist parents not in the menu land under "Other".
 */
export function buildMenuPermissionSections(permissionOptions = []) {
    const menu = getWmsMenuTree();
    const optionByValue = new Map();
    (permissionOptions || []).forEach((option) => {
        if (option?.value) optionByValue.set(option.value, option);
    });

    const groupedByParent = buildPermissionTree(permissionOptions);
    const groupByParentValue = new Map(groupedByParent.map((group) => [group.parentOption?.value, group]));

    const usedValues = new Set();

    const sections = (menu || [])
        .map((category) => ({
            id: category.id,
            name: category.name,
            tasks: (category.tasks || [])
                .map((task) => {
                    const group = groupByParentValue.get(task.permissionKey);
                    const parentOption = group?.parentOption || optionByValue.get(task.permissionKey);
                    if (!parentOption) return null;

                    usedValues.add(parentOption.value);
                    (group?.childOptions || []).forEach((child) => usedValues.add(child.value));

                    return {
                        permissionKey: task.permissionKey,
                        name: task.name,
                        parentOption,
                        childOptions: group?.childOptions || [],
                    };
                })
                .filter(Boolean),
        }))
        .filter((category) => category.tasks.length > 0);

    const otherTasks = [];
    groupedByParent.forEach((group) => {
        const parentValue = group.parentOption?.value;
        if (!parentValue || usedValues.has(parentValue)) return;

        usedValues.add(parentValue);
        (group.childOptions || []).forEach((child) => usedValues.add(child.value));

        otherTasks.push({
            permissionKey: parentValue,
            name: getPermissionDisplayLabel(group.parentOption),
            parentOption: group.parentOption,
            childOptions: group.childOptions || [],
        });
    });

    if (otherTasks.length) {
        sections.push({
            id: 'other',
            name: 'Other',
            tasks: otherTasks,
        });
    }

    return sections;
}

export function areAllParentsSelected(selectedPermissions = [], tasks = []) {
    const selectedSet = new Set(selectedPermissions || []);
    const parents = (tasks || []).map((task) => task?.parentOption?.value).filter(Boolean);
    return parents.length > 0 && parents.every((parent) => selectedSet.has(parent));
}

export function areAllPicklistParentsSelected(selectedPermissions = [], permissionOptions = []) {
    const selectedSet = new Set(selectedPermissions || []);
    const parents = (permissionOptions || [])
        .map((option) => option?.value)
        .filter((value) => value && !isChildPermissionValue(value));
    return parents.length > 0 && parents.every((parent) => selectedSet.has(parent));
}

function collectPermissionValuesFromTasks(tasks = []) {
    const values = [];
    (tasks || []).forEach((task) => {
        if (task?.parentOption?.value) values.push(task.parentOption.value);
        (task?.childOptions || []).forEach((child) => {
            if (child?.value) values.push(child.value);
        });
    });
    return values;
}

/** Select or clear every permission under the given tasks (category title click). */
export function toggleTasksScope(currentPermissions = [], tasks = []) {
    const scopeValues = collectPermissionValuesFromTasks(tasks);
    const scopeSet = new Set(scopeValues);
    if (!areAllParentsSelected(currentPermissions, tasks)) {
        return normalizePermissions([...(currentPermissions || []), ...scopeValues]);
    }
    return normalizePermissions((currentPermissions || []).filter((value) => !scopeSet.has(value)));
}
